import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatApiError } from "../lib/api";

const DEFAULT_OPTIONS = [
  "Advanced Diagnostics",
  "Performance Tuning & ECU Reflash",
  "Brake Service & Big-Brake Upgrades",
  "Factory Scheduled Maintenance",
  "Suspension, Alignment & Corner Balance",
  "Engine & Transmission Repair",
  "Part Enquiry",
  "Vehicle Purchase Enquiry",
  "Other",
];

const EMPTY = {
  name: "", phone: "", email: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "",
  service: "", preferred_date: "", preferred_time: "", message: "",
};

const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2";

export default function BookingDialog({ open, onOpenChange, prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [serviceOptions, setServiceOptions] = useState(DEFAULT_OPTIONS);
  const [slots, setSlots] = useState([]);
  const [taken, setTaken] = useState([]);

  useEffect(() => {
    api.get("/services")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) {
          setServiceOptions([...res.data.map((s) => s.name), "Vehicle Purchase Enquiry", "Other"]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.preferred_date) {
      setSlots([]);
      setTaken([]);
      return;
    }
    api.get(`/slots?date=${form.preferred_date}`)
      .then((res) => {
        setSlots(res.data.slots || []);
        setTaken(res.data.taken || []);
      })
      .catch(() => { setSlots([]); setTaken([]); });
  }, [form.preferred_date]);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...EMPTY,
        ...f,
        service: prefill?.service || "",
        message: prefill?.message || "",
      }));
    }
  }, [open, prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value, ...(key === "preferred_date" ? { preferred_time: "" } : {}) }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Request received", { description: "A Werkhaus advisor will contact you within one business day to confirm your slot." });
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error("Could not send request", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-panel border-[#263342] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-crimson font-semibold">SERVICE REQUEST</p>
          <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-3xl text-slate-100">
            Book your machine in
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Tell us about the vehicle and the work. We reply within one business day with a fixed quote.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-2 gap-5" data-testid="booking-form">
          <div>
            <label className={labelCls} htmlFor="bk-name">Full name *</label>
            <input id="bk-name" data-testid="booking-form-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Max Mustermann" />
          </div>
          <div>
            <label className={labelCls} htmlFor="bk-phone">Phone *</label>
            <input id="bk-phone" data-testid="booking-form-phone-input" required className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 555 010 2030" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="bk-email">Email *</label>
            <input id="bk-email" data-testid="booking-form-email-input" type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
          </div>
          <div>
            <label className={labelCls} htmlFor="bk-year">Vehicle year</label>
            <input id="bk-year" data-testid="booking-form-vehicle-year-input" className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2022" />
          </div>
          <div>
            <label className={labelCls} htmlFor="bk-make">Vehicle make</label>
            <input id="bk-make" data-testid="booking-form-vehicle-make-input" className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Porsche" />
          </div>
          <div>
            <label className={labelCls} htmlFor="bk-model">Vehicle model</label>
            <input id="bk-model" data-testid="booking-form-vehicle-model-input" className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="911 GT3" />
          </div>
          <div>
            <label className={labelCls} htmlFor="bk-date">Preferred date</label>
            <input id="bk-date" data-testid="booking-form-date-input" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.preferred_date} onChange={set("preferred_date")} />
          </div>
          {form.preferred_date && slots.length > 0 && (
            <div className="sm:col-span-2" data-testid="booking-slots">
              <label className={labelCls}>Pick a time slot</label>
              <div className="grid grid-cols-4 gap-2">
                {slots.map((slot) => {
                  const isTaken = taken.includes(slot);
                  const selected = form.preferred_time === slot;
                  return (
                    <button
                      type="button"
                      key={slot}
                      data-testid={`booking-slot-${slot.replace(":", "")}`}
                      disabled={isTaken}
                      onClick={() => setForm((f) => ({ ...f, preferred_time: slot }))}
                      className={`font-mono text-xs py-2.5 border transition-colors ${
                        isTaken
                          ? "border-[#263342] text-slate-600 line-through cursor-not-allowed"
                          : selected
                            ? "border-crimson bg-crimson/15 text-crimson"
                            : "border-[#263342] text-slate-300 hover:border-crimson/60 hover:text-crimson"
                      }`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="bk-service">Service required *</label>
            <select id="bk-service" data-testid="booking-form-service-select" required className={inputCls} value={form.service} onChange={set("service")}>
              <option value="" disabled>Select a service</option>
              {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="bk-message">Notes</label>
            <textarea id="bk-message" data-testid="booking-form-message-input" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Symptoms, mods, service history..." />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit"
              data-testid="booking-form-submit-button"
              disabled={submitting}
              className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-4 text-base hover:-translate-y-0.5 transition-transform hover:shadow-[0_0_32px_0_rgba(255,56,21,0.45)] disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting ? "Sending..." : "Submit Request"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
